import { DIST_EPSILON, DIST_EPSILON_SQUARED, PARAM_EPSILON, ZERO_EPSILON } from "./core";
import { line2Len } from "./line2";
import { vec2Dist, vec2DistSquared, vec2IsBetween, vec2Sub } from "./vec2";

const { abs } = Math;

export type CurveCurveIntersection = CurveCurveRangeIntersection | CurveCurvePointIntersection;

export class CurveCurveRangeIntersection {
  constructor(
    public startParam0: number,
    public startParam1: number,
    public startx: number,
    public starty: number,
    public endParam0: number,
    public endParam1: number,
    public endx: number,
    public endy: number,
  ) {
    // startParam0 should always be less than endParam0
    if (this.endParam0 < this.startParam0) {
      // swap params
      let t = this.startParam0;
      this.startParam0 = this.endParam0;
      this.endParam0 = t;

      t = this.startParam1;
      this.startParam1 = this.endParam1;
      this.endParam1 = t;

      // swap end points
      const tx = this.startx;
      const ty = this.starty;

      this.startx = this.endx;
      this.starty = this.endy;

      this.endx = tx;
      this.endy = ty;
    }
  }
}

export class CurveCurvePointIntersection {
  constructor(
    public param0: number,
    public param1: number,
    public x: number,
    public y: number,
  ) {}
}

function makeCurveCurveIntersectionFromRange(
  startParam0: number,
  startParam1: number,
  startx: number,
  starty: number,
  endParam0: number,
  endParam1: number,
  endx: number,
  endy: number,
): CurveCurveIntersection {
  if (abs(startParam0 - endParam0) < PARAM_EPSILON) {
    return new CurveCurvePointIntersection(startParam0, startParam1, startx, starty);
  }
  return new CurveCurveRangeIntersection(startParam0, startParam1, startx, starty, endParam0, endParam1, endx, endy);
}

function line2Param(ax: number, ay: number, _bx: number, _by: number, x: number, y: number): number {
  return vec2Dist(ax, ay, x, y);
}

export function intersectLine2Line2(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  epsilon: number = ZERO_EPSILON,
  dotEpsilon: number = 0,
  restrictToLineBounds = true,
  lineBoundToleranceCap = DIST_EPSILON,
): CurveCurveIntersection | null {
  // http://mathworld.wolfram.com/Line-LineIntersection.html
  const ax = x1 - x2;
  const ay = y1 - y2;
  const bx = x3 - x4;
  const by = y3 - y4;

  const denom = ax * by - ay * bx;

  // parallel case
  if (abs(denom) < epsilon) {
    const p1Incident = vec2IsBetween(x3, y3, x4, y4, x1, y1, epsilon, dotEpsilon);
    const p2Incident = vec2IsBetween(x3, y3, x4, y4, x2, y2, epsilon, dotEpsilon);
    const p3Incident = vec2IsBetween(x1, y1, x2, y2, x3, y3, epsilon, dotEpsilon);
    const p4Incident = vec2IsBetween(x1, y1, x2, y2, x4, y4, epsilon, dotEpsilon);

    const len1 = line2Len(x1, y1, x2, y2);
    const len2 = line2Len(x3, y3, x4, y4);

    const p1Param = line2Param(x3, y3, x4, y4, x1, y1);
    const p2Param = line2Param(x3, y3, x4, y4, x2, y2);

    const p3Param = line2Param(x1, y1, x2, y2, x3, y3);
    const p4Param = line2Param(x1, y1, x2, y2, x4, y4);

    // cases
    if (p1Incident && p2Incident) {
      return makeCurveCurveIntersectionFromRange(0, p1Param, x1, y1, len1, p2Param, x2, y2);
    } else if (p3Incident && p4Incident) {
      return makeCurveCurveIntersectionFromRange(p3Param, 0, x3, y3, p4Param, len2, x4, y4);
    } else if (p1Incident && p3Incident) {
      return makeCurveCurveIntersectionFromRange(0, p1Param, x1, y1, p3Param, 0, x3, y3);
    } else if (p1Incident && p4Incident) {
      return makeCurveCurveIntersectionFromRange(0, p1Param, x1, y1, p4Param, len2, x4, y4);
    } else if (p2Incident && p3Incident) {
      return makeCurveCurveIntersectionFromRange(len1, p2Param, x2, y2, p3Param, 0, x3, y3);
    } else if (p2Incident && p4Incident) {
      return makeCurveCurveIntersectionFromRange(len1, p2Param, x2, y2, p4Param, len2, x4, y4);
    }

    return null;
  }

  // transverse case
  const num0 = x1 * y2 - y1 * x2;
  const num1 = x3 * y4 - y3 * x4;
  const xnum = num0 * bx - ax * num1;
  const ynum = num0 * by - ay * num1;

  const x = xnum / denom;
  const y = ynum / denom;

  if (restrictToLineBounds) {
    // Determine whether the intersection is within both line segments. Using a higher
    // default epsilon here; vec2IsBetween uses it to determine whether the point lies
    // on the segment, which isn't a concern here since we're certain the computed
    // intersection lies on both lines.
    const c1 = vec2IsBetween(x1, y1, x2, y2, x, y, Math.max(epsilon, lineBoundToleranceCap), dotEpsilon);
    const c2 = vec2IsBetween(x3, y3, x4, y4, x, y, Math.max(epsilon, lineBoundToleranceCap), dotEpsilon);

    if (!c1 || !c2) {
      return null;
    }
  }

  return new CurveCurvePointIntersection(line2Param(x1, y1, x2, y2, x, y), line2Param(x3, y3, x4, y4, x, y), x, y);
}

enum Point2DPos {
  ON_LINE_OUT_OF_BOUNDS,
  ON_LINE_IN_BOUNDS,
  OFFSET_FROM_LINE,
}

interface RelationPointOnLineSegment2D {
  location: Point2DPos.ON_LINE_OUT_OF_BOUNDS | Point2DPos.ON_LINE_IN_BOUNDS;
  relDistance: number;
}

interface RelationPointOffLineSegment2D {
  location: Point2DPos.OFFSET_FROM_LINE;
  perpDistance: number;
}

type RelationPointLineSegment2D = RelationPointOnLineSegment2D | RelationPointOffLineSegment2D | undefined;

function pointOnLineSegment2D(
  x: number,
  y: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  coincidentTol: number = DIST_EPSILON,
  coincidentAngleTol: number = DIST_EPSILON,
  lengthTol: number = DIST_EPSILON_SQUARED,
): RelationPointLineSegment2D {
  const len13 = vec2Dist(x, y, x1, y1);

  if (len13 < lengthTol) {
    return { location: Point2DPos.ON_LINE_IN_BOUNDS, relDistance: 0 };
  }

  const len12 = vec2Dist(x2, y2, x1, y1);

  if (len12 < lengthTol) {
    return undefined;
  }

  const [v13x, v13y] = vec2Sub(x, y, x1, y1);
  const [v12x, v12y] = vec2Sub(x2, y2, x1, y1);
  const dp = v13x * v12x + v13y * v12y;

  const cosang = dp / (len13 * len12);

  if (cosang < 1 - coincidentAngleTol && cosang > -1 + coincidentAngleTol) {
    return { location: Point2DPos.OFFSET_FROM_LINE, perpDistance: len13 * cosang };
  }

  const absoluteDistance = len13 * Math.sign(cosang);
  const relDistance = absoluteDistance / len12;

  if (relDistance >= -coincidentTol && relDistance <= 1.0 + coincidentTol) {
    return { location: Point2DPos.ON_LINE_IN_BOUNDS, relDistance };
  } else {
    return { location: Point2DPos.ON_LINE_OUT_OF_BOUNDS, relDistance };
  }
}
/**
 * The following function finds intersection between two line segments in 2D space.
 *  The function returns null if there is no intersection within the given tolerance and bounds of the two line segments.
 *  This is an improvement over existing line segment intersection function in the following manner:
 *  1. The tolerances are more strict and the function is designed to be less suscpetible to small floating point errors.
 *  2. Uses semantic based approach in determining the overlap.
 *  3. The function also minimizes the number of floating point operations for parallel lines since it uses semantic checks
 * to determine the intersections.
 * Overall, this function is a more robust alternative to intersectLine2Line2 in calculating line segements intersections.
 * The older implementation is still kept for backward compatibility.
 */
export function intersectLineSegments2D(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  proximityTol: number = DIST_EPSILON_SQUARED,
  parallelLineTol: number = DIST_EPSILON_SQUARED,
): CurveCurveIntersection | null {
  const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  const l1 = vec2DistSquared(x1, y1, x2, y2);
  const l2 = vec2DistSquared(x3, y3, x4, y4);

  if (Math.abs(d) < parallelLineTol * l1 * l2) {
    //parallel
    const line2Pt1Line1Relation = pointOnLineSegment2D(x3, y3, x1, y1, x2, y2, proximityTol);
    const line2Pt2Line1Relation = pointOnLineSegment2D(x4, y4, x1, y1, x2, y2, proximityTol);

    if (!line2Pt1Line1Relation || !line2Pt2Line1Relation) {
      return null; // either one of the line or both are practically too small to be considered
    }

    if (
      line2Pt1Line1Relation.location === Point2DPos.OFFSET_FROM_LINE &&
      line2Pt2Line1Relation.location === Point2DPos.OFFSET_FROM_LINE
    ) {
      return null; // both points are off the line i.e. parallel but not overlapping
    }

    //the lines were computed to be parallel but somehow one point is not on the line
    //this is still an inbound intersection. This should not happen in a well formed input.
    if (
      line2Pt1Line1Relation.location === Point2DPos.OFFSET_FROM_LINE &&
      line2Pt2Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS
    ) {
      return new CurveCurvePointIntersection(
        line2Param(x1, y1, x2, y2, x4, y4),
        line2Param(x3, y3, x4, y4, x4, y4),
        x4,
        y4,
      ); // one point is off the line
    }

    //the lines were computed to be parallel but somehow one point is not on the line (passed tolerance check)
    //this is still an inbound intersection. This should not happen in a well formed input.
    if (
      line2Pt1Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS &&
      line2Pt2Line1Relation.location === Point2DPos.OFFSET_FROM_LINE
    ) {
      return new CurveCurvePointIntersection(line2Param(x1, y1, x2, y2, x3, y3), 0, x3, y3); // one point is off the line
    }

    //this is a case where both points from one line are on the other segment i.e. the lines are overlapping
    //line1 is completely on line2
    if (
      line2Pt1Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS &&
      line2Pt2Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS
    ) {
      return makeCurveCurveIntersectionFromRange(
        line2Param(x1, y1, x2, y2, x3, y3),
        0,
        x3,
        y3,
        line2Param(x1, y1, x2, y2, x4, y4),
        line2Param(x3, y3, x4, y4, x4, y4),
        x4,
        y4,
      );
    }

    if (
      line2Pt1Line1Relation.location === Point2DPos.ON_LINE_OUT_OF_BOUNDS &&
      line2Pt2Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS
    ) {
      if (line2Pt1Line1Relation.relDistance < -proximityTol && line2Pt2Line1Relation.relDistance > -proximityTol) {
        return makeCurveCurveIntersectionFromRange(
          0,
          line2Param(x3, y3, x4, y4, x1, y1),
          x1,
          y1,
          line2Param(x1, y1, x2, y2, x4, y4),
          line2Param(x3, y3, x4, y4, x4, y4),
          x4,
          y4,
        );
      } else if (
        line2Pt1Line1Relation.relDistance > 1 - proximityTol &&
        line2Pt2Line1Relation.relDistance > -proximityTol
      ) {
        return makeCurveCurveIntersectionFromRange(
          line2Param(x1, y1, x2, y2, x4, y4),
          line2Param(x3, y3, x4, y4, x4, y4),
          x4,
          y4,
          line2Param(x1, y1, x2, y2, x2, y2),
          line2Param(x3, y3, x4, y4, x2, y2),
          x2,
          y2,
        );
      }
    }

    if (
      line2Pt1Line1Relation.location === Point2DPos.ON_LINE_IN_BOUNDS &&
      line2Pt2Line1Relation.location === Point2DPos.ON_LINE_OUT_OF_BOUNDS
    ) {
      if (line2Pt1Line1Relation.relDistance > -proximityTol && line2Pt2Line1Relation.relDistance > 1 - proximityTol) {
        return makeCurveCurveIntersectionFromRange(
          line2Param(x1, y1, x2, y2, x3, y3),
          line2Param(x3, y3, x4, y4, x3, y3),
          x3,
          y3,
          line2Param(x1, y1, x2, y2, x2, y2),
          line2Param(x3, y3, x4, y4, x2, y2),
          x2,
          y2,
        );
      } else if (
        line2Pt1Line1Relation.relDistance > -proximityTol &&
        line2Pt2Line1Relation.relDistance < -proximityTol
      ) {
        return makeCurveCurveIntersectionFromRange(
          line2Param(x1, y1, x2, y2, x1, y1),
          line2Param(x3, y3, x4, y4, x1, y1),
          x1,
          y1,
          line2Param(x1, y1, x2, y2, x3, y3),
          line2Param(x3, y3, x4, y4, x3, y3),
          x3,
          y3,
        );
      }
    }

    if (
      line2Pt1Line1Relation.location === Point2DPos.ON_LINE_OUT_OF_BOUNDS &&
      line2Pt2Line1Relation.location === Point2DPos.ON_LINE_OUT_OF_BOUNDS
    ) {
      // Both points of line 2 are out of line 1's bounds. A real overlap only
      // exists when they straddle line 1 (one before x1, one past x2). If both
      // lie on the same side, the two collinear segments don't actually share
      // any points.
      const r1 = line2Pt1Line1Relation.relDistance;
      const r2 = line2Pt2Line1Relation.relDistance;
      if ((r1 < 0 && r2 < 0) || (r1 > 1 && r2 > 1)) {
        return null;
      }
      // Line 2 contains line 1: the overlap range is line 1 itself.
      return makeCurveCurveIntersectionFromRange(
        0,
        line2Param(x3, y3, x4, y4, x1, y1),
        x1,
        y1,
        line2Param(x1, y1, x2, y2, x2, y2),
        line2Param(x3, y3, x4, y4, x2, y2),
        x2,
        y2,
      );
    }

    return null;
  }
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / d;

  if (t < -proximityTol || t > 1 + proximityTol) {
    return null;
  }

  const u = ((x1 - x3) * (y1 - y2) - (y1 - y3) * (x1 - x2)) / d;

  if (u < -proximityTol || u > 1 + proximityTol) {
    return null;
  }

  const x0 = x1 + t * (x2 - x1);
  const y0 = y1 + t * (y2 - y1);

  return new CurveCurvePointIntersection(
    line2Param(x1, y1, x2, y2, x0, y0),
    line2Param(x3, y3, x4, y4, x0, y0),
    x0,
    y0,
  );
}
